import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, BookOpen } from "lucide-react";

import { cmsApi } from "@profitlens/api-client";
import { Button } from "@profitlens/ui/button";
import { Spinner } from "@profitlens/ui/spinner";
import { normalizeLanguage } from "@/i18n";

/**
 * 公开帮助中心：`/help` 列表，`/help/:slug` 详情。
 * 内容来自管理后台「官网管理 → 帮助文档」（仅展示已发布且当前语言的条目）。
 */
export function HelpPage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const locale = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

  const list = useQuery({
    queryKey: ["cms", "help-docs", locale],
    queryFn: () => cmsApi.helpDocs(locale),
    enabled: !slug,
  });
  const detail = useQuery({
    queryKey: ["cms", "help-doc", locale, slug],
    queryFn: () => cmsApi.helpDoc(slug!, locale),
    enabled: Boolean(slug),
  });

  const loading = slug ? detail.isLoading : list.isLoading;

  return (
    <div className="min-h-dvh bg-background">
      <header className="border-b border-border px-4 py-3 sm:px-6">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-sm font-bold text-brand-fg">
              PL
            </span>
            <span className="text-sm font-semibold">{t("app.name")}</span>
          </Link>
          <span className="flex items-center gap-1.5 text-xs text-muted">
            <BookOpen className="h-3.5 w-3.5" />
            {t("landing.helpTitle")}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        {loading ? (
          <div className="grid place-items-center py-16">
            <Spinner className="h-5 w-5" />
          </div>
        ) : slug ? (
          detail.data ? (
            <article className="space-y-4">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/help">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t("landing.helpBack")}
                </Link>
              </Button>
              <h1 className="text-2xl font-semibold tracking-tight">{detail.data.title}</h1>
              {detail.data.summary ? (
                <p className="text-sm text-muted">{detail.data.summary}</p>
              ) : null}
              <div className="text-sm whitespace-pre-wrap">{detail.data.body}</div>
            </article>
          ) : (
            <p className="py-16 text-center text-sm text-muted">{t("landing.docMissing")}</p>
          )
        ) : (list.data ?? []).length === 0 ? (
          <p className="py-16 text-center text-sm text-muted">{t("landing.helpEmpty")}</p>
        ) : (
          <div className="space-y-4">
            <h1 className="text-2xl font-semibold tracking-tight">{t("landing.helpTitle")}</h1>
            <p className="text-sm text-muted">{t("landing.helpSubtitle")}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {list.data?.map((doc) => (
                <Link
                  key={doc.id}
                  to={`/help/${doc.slug}`}
                  className="rounded-xl border border-border bg-surface p-4 transition-colors hover:border-brand"
                >
                  <p className="text-sm font-semibold">{doc.title}</p>
                  {doc.summary ? (
                    <p className="mt-1.5 line-clamp-2 text-sm text-muted">{doc.summary}</p>
                  ) : null}
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
